import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ShoppingCart, ChevronDown } from 'lucide-react';
import { useCart } from '../context/CartContext';
import logoWbg from '../assets/logo.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [eyeglassesDropdownOpen, setEyeglassesDropdownOpen] = useState(false);
  const [sunglassesDropdownOpen, setSunglassesDropdownOpen] = useState(false);
  const [lensesDropdownOpen, setLensesDropdownOpen] = useState(false);
  const [mobileEyeglassesDropdownOpen, setMobileEyeglassesDropdownOpen] = useState(false);
  const [mobileSunglassesDropdownOpen, setMobileSunglassesDropdownOpen] = useState(false);
  const [mobileLensesDropdownOpen, setMobileLensesDropdownOpen] = useState(false);
  const location = useLocation();
  const { getCartCount, openCart } = useCart();
  const eyeglassesDropdownRef = useRef(null);
  const sunglassesDropdownRef = useRef(null);
  const lensesDropdownRef = useRef(null);

  const navItems = [
    { 
      name: 'Eyeglasses', 
      path: '/eyeglasses',
      hasDropdown: true,
      dropdownItems: [
        { name: 'Metal Frames', path: '/eyeglasses?category=metalframe' },
        { name: 'Plastic Frames', path: '/eyeglasses?category=plastic' },
        { name: 'Unbreakable frame', path: '/eyeglasses?category=unbreakableframe' },
      ]
    },
    { 
      name: 'Sunglasses', 
      path: '/sunglasses',
      hasDropdown: true,
      dropdownItems: [
        { name: 'Men', path: '/sunglasses?category=men' },
        { name: 'Women', path: '/sunglasses?category=women' },
        { name: 'Unisex', path: '/sunglasses?category=unisex' },
      ]
    },
    { 
      name: 'Lenses', 
      path: '/lenses',
      hasDropdown: true,
      dropdownItems: [
        { name: 'DAHAB', path: '/lenses?category=dahab' },
        { name: 'SIGMA', path: '/lenses?category=sigma' },
        { name: 'COMFORT LENSES', path: '/lenses?category=comfortlenses' },
      ]
    },
    { name: 'Kids', path: '/kids' },
    { name: 'Accessories', path: '/accessories' },
    // { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => {
    if (path.includes('?')) {
      const basePath = path.split('?')[0];
      return location.pathname === basePath;
    }
    return location.pathname === path;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (eyeglassesDropdownRef.current && !eyeglassesDropdownRef.current.contains(event.target)) {
        setEyeglassesDropdownOpen(false);
      }
      if (sunglassesDropdownRef.current && !sunglassesDropdownRef.current.contains(event.target)) {
        setSunglassesDropdownOpen(false);
      }
      if (lensesDropdownRef.current && !lensesDropdownRef.current.contains(event.target)) {
        setLensesDropdownOpen(false);
      }
    };

    if (eyeglassesDropdownOpen || sunglassesDropdownOpen || lensesDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [eyeglassesDropdownOpen, sunglassesDropdownOpen, lensesDropdownOpen]);

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Mobile Menu Button - Left */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-primary focus:outline-none focus:text-primary"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Logo - Center on Mobile, Left on Desktop */}
          <Link to="/" className="flex flex-col items-center md:flex-row md:space-x-2">
            <img 
              src={logoWbg} 
              alt="Mughal Ainak Mahal" 
              className="h-6 md:h-8 w-auto"
            />
            <div>
              <h1 className="text-[10px] md:text-sm font-semibold tracking-tight text-center md:text-left">
                <span className="text-black">Mughal</span>{' '}
                <span className="text-primary">Ainak</span>{' '}
                <span className="text-black">Mahal</span>
              </h1>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              item.hasDropdown ? (
                <div 
                  key={item.name} 
                  className="relative"
                  ref={item.name === 'Eyeglasses' ? eyeglassesDropdownRef : item.name === 'Sunglasses' ? sunglassesDropdownRef : item.name === 'Lenses' ? lensesDropdownRef : null}
                  onMouseEnter={() => {
                    if (item.name === 'Eyeglasses') setEyeglassesDropdownOpen(true);
                    if (item.name === 'Sunglasses') setSunglassesDropdownOpen(true);
                    if (item.name === 'Lenses') setLensesDropdownOpen(true);
                  }}
                  onMouseLeave={() => {
                    if (item.name === 'Eyeglasses') setEyeglassesDropdownOpen(false);
                    if (item.name === 'Sunglasses') setSunglassesDropdownOpen(false);
                    if (item.name === 'Lenses') setLensesDropdownOpen(false);
                  }}
                >
                  <div className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                    isActive(item.path)
                      ? 'text-primary bg-primary bg-opacity-10'
                      : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                  }`}>
                    <Link
                      to={item.path}
                      className="flex items-center space-x-1"
                    >
                      <span>{item.name}</span>
                    </Link>
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${
                      (item.name === 'Eyeglasses' && eyeglassesDropdownOpen) || 
                      (item.name === 'Sunglasses' && sunglassesDropdownOpen) ||
                      (item.name === 'Lenses' && lensesDropdownOpen)
                        ? 'rotate-180' : ''
                    }`} />
                  </div>
                  
                  {((item.name === 'Eyeglasses' && eyeglassesDropdownOpen) || 
                    (item.name === 'Sunglasses' && sunglassesDropdownOpen) ||
                    (item.name === 'Lenses' && lensesDropdownOpen)) && (
                    <div className="absolute top-full left-0 pt-1 w-48 z-50">
                      <div className="bg-white border border-gray-200 rounded-lg shadow-lg">
                        {item.dropdownItems.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.name}
                            to={dropdownItem.path}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary transition-colors duration-200 first:rounded-t-lg last:rounded-b-lg"
                          >
                            {dropdownItem.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                    isActive(item.path)
                      ? 'text-primary bg-primary bg-opacity-10'
                      : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                  }`}
                >
                  {item.name}
                </Link>
              )
            ))}
          </div>

          {/* Desktop Contact Info & Cart */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href="tel:+923136261452"
              className="flex items-center space-x-2 text-primary hover:text-primary-dark transition-colors duration-200"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">0313 626 1452</span>
            </a>
            <button
              type="button"
              onClick={openCart}
              className="relative flex items-center space-x-2 text-primary hover:text-primary-dark transition-colors duration-200"
            >
              <ShoppingCart className="w-5 h-5" />
              <span className="text-sm font-medium">Cart</span>
              {getCartCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getCartCount()}
                </span>
              )}
            </button>
          </div>

          {/* Mobile Cart Button - Right */}
          <div className="md:hidden">
            <button
              type="button"
              onClick={openCart}
              className="relative text-primary hover:text-primary-dark transition-colors duration-200"
            >
              <ShoppingCart className="w-6 h-6" />
              {getCartCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getCartCount()}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
              {navItems.map((item) => (
                item.hasDropdown ? (
                  <div key={item.name}>
                    <button
                      onClick={() => {
                        if (item.name === 'Eyeglasses') {
                          setMobileEyeglassesDropdownOpen(!mobileEyeglassesDropdownOpen);
                        }
                        if (item.name === 'Sunglasses') {
                          setMobileSunglassesDropdownOpen(!mobileSunglassesDropdownOpen);
                        }
                        if (item.name === 'Lenses') {
                          setMobileLensesDropdownOpen(!mobileLensesDropdownOpen);
                        }
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                        isActive(item.path)
                          ? 'text-primary bg-primary bg-opacity-10'
                          : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                      }`}
                    >
                      <span>{item.name}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${
                        (item.name === 'Eyeglasses' && mobileEyeglassesDropdownOpen) ||
                        (item.name === 'Sunglasses' && mobileSunglassesDropdownOpen) ||
                        (item.name === 'Lenses' && mobileLensesDropdownOpen)
                          ? 'rotate-180' : ''
                      }`} />
                    </button>
                    
                    {((item.name === 'Eyeglasses' && mobileEyeglassesDropdownOpen) ||
                      (item.name === 'Sunglasses' && mobileSunglassesDropdownOpen) ||
                      (item.name === 'Lenses' && mobileLensesDropdownOpen)) && (
                      <div className="pl-6 space-y-1 mt-1">
                        {item.dropdownItems.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.name}
                            to={dropdownItem.path}
                            className="block px-3 py-2 rounded-md text-sm text-gray-700 hover:text-primary hover:bg-gray-50 transition-colors duration-200"
                            onClick={() => {
                              setIsOpen(false);
                              setMobileEyeglassesDropdownOpen(false);
                              setMobileSunglassesDropdownOpen(false);
                              setMobileLensesDropdownOpen(false);
                            }}
                          >
                            {dropdownItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                      isActive(item.path)
                        ? 'text-primary bg-primary bg-opacity-10'
                        : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                )
              ))}
              <Link
                to="/reviews"
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                  location.pathname === '/reviews'
                    ? 'text-primary bg-primary bg-opacity-10'
                    : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                }`}
                onClick={() => setIsOpen(false)}
              >
                Reviews
              </Link>
              <a
                href="tel:+923136261452"
                className="block px-3 py-2 rounded-md text-base font-medium text-primary hover:bg-gray-50 transition-colors duration-200"
                onClick={() => setIsOpen(false)}
              >
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>0313 626 1452</span>
                </div>
              </a>
              <button
                type="button"
                className="block px-3 py-2 rounded-md text-base font-medium text-primary hover:bg-gray-50 transition-colors duration-200"
                onClick={() => {
                  setIsOpen(false);
                  openCart();
                }}
              >
                <div className="flex items-center space-x-2">
                  <ShoppingCart className="w-4 h-4" />
                  <span>Cart ({getCartCount()})</span>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar; 
